// Group, pace and progress analysis of a shooter's own Megalink shots for the
// profile «Skudd» tab — Tilleggsmodul (C, shooting). Pure, no DOM. Input is the
// buildShotHistory() output (lib/shotHistory.js): a series type's shots, and its
// Lag rows with lagSeriesGroups() split times. Positions are tenths of a
// millimetre, y up, like megalinkShot.x / .y; every result is in millimetres.

import { TARGETS, ringValue } from './pistolTargets';

export const RAW_PER_MM = 10;
// The second half of a series is «faster» / «slower» when its average split
// differs from the first half's by more than this fraction.
export const PACE_TREND_FRACTION = 0.15;
// The newest Lag is «better» / «worse» when its mean radius differs from the
// mean of the earlier Lag by more than this fraction.
export const PROGRESS_TREND_FRACTION = 0.1;
// The covariance ellipse is drawn at this many standard deviations.
export const ELLIPSE_SIGMA = 2;
// Pace against hit needs this many timed shots before a correlation is shown.
export const PACE_HIT_MIN_POINTS = 4;
// |Pearson r| above this is read as a link between split time and distance.
export const PACE_HIT_LINK_R = 0.3;
// Lag totals are «stable» when their standard deviation is under this share of the mean.
export const STABILITY_FRACTION = 0.03;
// The density heatmap replaces the dots from this many shots on one series target.
export const HEATMAP_MIN_SHOTS = 100;
// Heatmap cell side as a share of the target's ring step.
export const HEATMAP_CELL_RING_FRACTION = 0.25;
const ELLIPSE_MIN_SHOTS = 3;
const CEP_FRACTION = 0.95;

function positioned(shot) {
    return !!shot && Number.isFinite(shot.x) && Number.isFinite(shot.y);
}

function mean(list, pick) {
    return list.reduce((acc, item) => acc + pick(item), 0) / list.length;
}

function sampleSd(list, pick, center) {
    if (list.length < 2) return 0;
    const sum = list.reduce((acc, item) => acc + (pick(item) - center) ** 2, 0);
    return Math.sqrt(sum / (list.length - 1));
}

function percentile(sorted, fraction) {
    if (sorted.length === 0) return 0;
    const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil(fraction * sorted.length) - 1));
    return sorted[idx];
}

// { a, b, angleDeg }: semi-axes (mm) of the ELLIPSE_SIGMA covariance ellipse and
// the angle of the long axis from the x axis, counter-clockwise with y up.
function covarianceEllipse(points, cx, cy) {
    const n = points.length;
    if (n < ELLIPSE_MIN_SHOTS) return null;
    let varX = 0;
    let varY = 0;
    let cov = 0;
    points.forEach(p => {
        varX += (p.x - cx) ** 2;
        varY += (p.y - cy) ** 2;
        cov += (p.x - cx) * (p.y - cy);
    });
    varX /= n - 1;
    varY /= n - 1;
    cov /= n - 1;
    const trace = varX + varY;
    const det = varX * varY - cov * cov;
    const disc = Math.sqrt(Math.max(0, (trace * trace) / 4 - det));
    const l1 = trace / 2 + disc;
    const l2 = Math.max(0, trace / 2 - disc);
    const angleDeg = (0.5 * Math.atan2(2 * cov, varX - varY) * 180) / Math.PI;
    return { a: ELLIPSE_SIGMA * Math.sqrt(l1), b: ELLIPSE_SIGMA * Math.sqrt(l2), angleDeg };
}

function centeringGain(points, target) {
    const cx = mean(points, p => p.x);
    const cy = mean(points, p => p.y);
    const actual = points.reduce((acc, p) => acc + ringValue(target, p.x * RAW_PER_MM, p.y * RAW_PER_MM), 0);
    const centered = points.reduce((acc, p) => acc + ringValue(
        target, (p.x - cx) * RAW_PER_MM, (p.y - cy) * RAW_PER_MM,
    ), 0);
    return { actual, centered, delta: centered - actual };
}

function toMm(shots) {
    return (Array.isArray(shots) ? shots : [])
        .filter(positioned)
        .map(s => ({
            x: s.x / RAW_PER_MM, y: s.y / RAW_PER_MM, value: s.value, decimalValue: s.decimalValue, innerTen: s.innerTen,
        }));
}

function groupMpi(shots) {
    const points = toMm(shots);
    if (points.length === 0) return null;
    return { x: mean(points, p => p.x), y: mean(points, p => p.y), count: points.length };
}

function groupsOf(lag) {
    return lag && Array.isArray(lag.groups) ? lag.groups.filter(g => g && Array.isArray(g.shots)) : [];
}

/**
 * «Your group»: where the shots of one series type land.
 * @param shots  the series' shots (tenths of a millimetre)
 * @param target 'precision' | 'duel' | 'air' (lib/pistolTargets.js TARGETS)
 * @returns null without positioned shots, else {
 *   count, mpi: { x, y } mm from the centre (y up), offsetMm,
 *   meanRadiusMm, cep95Mm, sdXMm, sdYMm, extentXMm, extentYMm,
 *   ellipse: null | { cx, cy, a, b, angleDeg } (mm),
 *   histogram: [{ value, count }] lowest ring … 10 from the stored values,
 *   min, max, avg (stored ring values; null without values),
 *   gain: { actual, centered, delta } — ring score from the positions as shot vs. the group centred,
 * }
 */
export function groupStats(shots, target) {
    const points = toMm(shots);
    if (points.length === 0) return null;
    const t = TARGETS[target] || TARGETS.duel;
    const cx = mean(points, p => p.x);
    const cy = mean(points, p => p.y);
    const radii = points.map(p => Math.sqrt((p.x - cx) ** 2 + (p.y - cy) ** 2)).sort((a, b) => a - b);
    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);
    const ellipse = covarianceEllipse(points, cx, cy);
    const valued = points.filter(p => Number.isFinite(p.value));
    const histogram = [];
    for (let value = t.lowestRing; value <= 10; value += 1) {
        histogram.push({ value, count: valued.filter(p => p.value === value).length });
    }
    return {
        count: points.length,
        mpi: { x: cx, y: cy },
        offsetMm: Math.sqrt(cx * cx + cy * cy),
        meanRadiusMm: mean(radii, r => r),
        cep95Mm: percentile(radii, CEP_FRACTION),
        sdXMm: sampleSd(points, p => p.x, cx),
        sdYMm: sampleSd(points, p => p.y, cy),
        extentXMm: Math.max(...xs) - Math.min(...xs),
        extentYMm: Math.max(...ys) - Math.min(...ys),
        ellipse: ellipse ? { cx, cy, ...ellipse } : null,
        histogram,
        min: valued.length ? Math.min(...valued.map(p => p.value)) : null,
        max: valued.length ? Math.max(...valued.map(p => p.value)) : null,
        avg: valued.length ? mean(valued, p => p.value) : null,
        gain: centeringGain(points, target),
    };
}

function halfTrend(first, second, fraction) {
    if (second < first * (1 - fraction)) return 'faster';
    if (second > first * (1 + fraction)) return 'slower';
    return 'steady';
}

/**
 * «Your pace»: the average split time at each shot position of the series,
 * across every Lag given, from Megalink's own shot times.
 * @param lags buildShotHistory() series.lags — each with groups[].shots[].split (1/100 s)
 * @returns null without a single split, else {
 *   splitCount, avgCentis,
 *   positions: [{ index, avgCentis, count }] — index 1 = split from shot 1 to shot 2,
 *   firstHalfCentis, secondHalfCentis (averages over the first / second half of the positions),
 *   trend: 'faster' | 'slower' | 'steady' — the second half against the first,
 * }
 */
export function paceStats(lags) {
    const sums = [];
    let total = 0;
    let splitCount = 0;
    (Array.isArray(lags) ? lags : []).forEach(lag => {
        (lag && Array.isArray(lag.groups) ? lag.groups : []).forEach(group => {
            (group && Array.isArray(group.shots) ? group.shots : []).forEach((shot, i) => {
                if (i === 0 || !Number.isInteger(shot.split)) return;
                if (!sums[i - 1]) sums[i - 1] = { sum: 0, count: 0 };
                sums[i - 1].sum += shot.split;
                sums[i - 1].count += 1;
                total += shot.split;
                splitCount += 1;
            });
        });
    });
    if (splitCount === 0) return null;
    const positions = sums
        .map((entry, i) => (entry ? { index: i + 1, avgCentis: Math.round(entry.sum / entry.count), count: entry.count } : null))
        .filter(Boolean);
    const cut = Math.ceil(positions.length / 2);
    const first = positions.slice(0, cut);
    const second = positions.slice(cut);
    const firstHalfCentis = Math.round(mean(first, p => p.avgCentis));
    const secondHalfCentis = second.length ? Math.round(mean(second, p => p.avgCentis)) : firstHalfCentis;
    return {
        splitCount,
        avgCentis: Math.round(total / splitCount),
        positions,
        firstHalfCentis,
        secondHalfCentis,
        trend: second.length ? halfTrend(firstHalfCentis, secondHalfCentis, PACE_TREND_FRACTION) : 'steady',
    };
}

/**
 * «Your progress»: one point per Lag with at least two positioned shots, oldest first.
 * @param lags   buildShotHistory() series.lags
 * @param target 'precision' | 'duel' | 'air'
 * @returns [{ date, title, stageName, conventionId, isCompetition, count, meanRadiusMm, cep95Mm, offsetMm, gainDelta,
 *            mpiX, mpiY (mm), total (sum of ring values), decimalAvg (null without decimals),
 *            innerTenShare (0..1, null when no shot says) }]
 */
export function progressSeries(lags, target) {
    return (Array.isArray(lags) ? lags : [])
        .map(lag => {
            const stats = lag ? groupStats(lag.shots, target) : null;
            if (!stats || stats.count < 2) return null;
            const points = toMm(lag.shots);
            const decimals = points.filter(p => Number.isFinite(p.decimalValue));
            const flagged = points.filter(p => typeof p.innerTen === 'boolean');
            return {
                date: lag.date || null,
                title: lag.title || '',
                stageName: lag.stageName || '',
                conventionId: lag.conventionId || 0,
                isCompetition: !!lag.isCompetition,
                count: stats.count,
                meanRadiusMm: stats.meanRadiusMm,
                cep95Mm: stats.cep95Mm,
                offsetMm: stats.offsetMm,
                gainDelta: stats.gain.delta,
                mpiX: stats.mpi.x,
                mpiY: stats.mpi.y,
                total: points.reduce((acc, p) => acc + (Number.isFinite(p.value) ? p.value : 0), 0),
                decimalAvg: decimals.length ? mean(decimals, p => p.decimalValue) : null,
                innerTenShare: flagged.length ? flagged.filter(p => p.innerTen).length / flagged.length : null,
            };
        })
        .filter(Boolean)
        .sort((a, b) => Date.parse(a.date || 0) - Date.parse(b.date || 0));
}

/**
 * The newest Lag's mean radius against the mean of the earlier Lag.
 * @returns null under two points, else { direction: 'better' | 'worse' | 'steady', latest, earlier } (mm)
 */
export function progressTrend(points) {
    const list = (Array.isArray(points) ? points : []).filter(p => p && Number.isFinite(p.meanRadiusMm));
    if (list.length < 2) return null;
    const latest = list[list.length - 1].meanRadiusMm;
    const earlier = mean(list.slice(0, -1), p => p.meanRadiusMm);
    let direction = 'steady';
    if (latest < earlier * (1 - PROGRESS_TREND_FRACTION)) direction = 'better';
    else if (latest > earlier * (1 + PROGRESS_TREND_FRACTION)) direction = 'worse';
    return { direction, latest, earlier };
}

/**
 * «Shot order»: the mean point of shot 1, 2, 3 … of a series across every Lag group.
 * @returns null unless some position has two Lag, else [{ index (1-based), mpi: { x, y } mm, count }]
 */
export function shotOrderDrift(lags) {
    const buckets = [];
    (Array.isArray(lags) ? lags : []).forEach(lag => groupsOf(lag).forEach(group => {
        group.shots.forEach((shot, i) => {
            if (!positioned(shot)) return;
            if (!buckets[i]) buckets[i] = [];
            buckets[i].push(shot);
        });
    }));
    const out = buckets
        .map((list, i) => (list && list.length >= 2 ? { index: i + 1, mpi: groupMpi(list), count: list.length } : null))
        .filter(Boolean)
        .map(p => ({ index: p.index, mpi: { x: p.mpi.x, y: p.mpi.y }, count: p.count }));
    return out.length ? out : null;
}

function pearson(points, px, py) {
    const n = points.length;
    const mx = mean(points, px);
    const my = mean(points, py);
    let sxy = 0;
    let sxx = 0;
    let syy = 0;
    points.forEach(p => {
        sxy += (px(p) - mx) * (py(p) - my);
        sxx += (px(p) - mx) ** 2;
        syy += (py(p) - my) ** 2;
    });
    if (n < 2 || sxx === 0 || syy === 0) return 0;
    return sxy / Math.sqrt(sxx * syy);
}

/**
 * «Pace against hit»: every timed shot's split time against its distance from
 * its own Lag group's mean point.
 * @returns null under PACE_HIT_MIN_POINTS points, else {
 *   points: [{ splitCentis, radiusMm }], r (Pearson),
 *   link: 'fastCloser' | 'slowCloser' | 'none',
 *   fastAvgMm, slowAvgMm — mean distance of the shots at / under the median split and above it,
 * }
 */
export function paceVsHit(lags) {
    const points = [];
    (Array.isArray(lags) ? lags : []).forEach(lag => groupsOf(lag).forEach(group => {
        const mpi = groupMpi(group.shots);
        if (!mpi) return;
        group.shots.forEach(shot => {
            if (!positioned(shot) || !Number.isInteger(shot.split)) return;
            const dx = shot.x / RAW_PER_MM - mpi.x;
            const dy = shot.y / RAW_PER_MM - mpi.y;
            points.push({ splitCentis: shot.split, radiusMm: Math.sqrt(dx * dx + dy * dy) });
        });
    }));
    if (points.length < PACE_HIT_MIN_POINTS) return null;
    const r = pearson(points, p => p.splitCentis, p => p.radiusMm);
    const sorted = [...points].sort((a, b) => a.splitCentis - b.splitCentis);
    const median = sorted[Math.floor((sorted.length - 1) / 2)].splitCentis;
    const fast = points.filter(p => p.splitCentis <= median);
    const slow = points.filter(p => p.splitCentis > median);
    let link = 'none';
    if (r > PACE_HIT_LINK_R) link = 'fastCloser';
    else if (r < -PACE_HIT_LINK_R) link = 'slowCloser';
    return {
        points,
        r,
        link,
        fastAvgMm: fast.length ? mean(fast, p => p.radiusMm) : null,
        slowAvgMm: slow.length ? mean(slow, p => p.radiusMm) : null,
    };
}

/**
 * «Series types»: mean radius per program series type (sighters skipped, empty skipped), program order.
 * @param program buildShotHistory() program
 * @returns [{ key, label, count, meanRadiusMm, cep95Mm }]
 */
export function seriesTypeComparison(program) {
    return (program && Array.isArray(program.series) ? program.series : [])
        .filter(s => s && !s.sighter)
        .map(s => {
            const stats = groupStats(s.shots, s.target);
            return stats ? {
                key: s.key, label: s.label, count: stats.count, meanRadiusMm: stats.meanRadiusMm, cep95Mm: stats.cep95Mm,
            } : null;
        })
        .filter(Boolean);
}

function pooledStats(lags, target) {
    if (lags.length === 0) return null;
    const shots = lags.reduce((all, lag) => all.concat(Array.isArray(lag.shots) ? lag.shots : []), []);
    const stats = groupStats(shots, target);
    return stats ? { ...stats, lagCount: lags.length } : null;
}

/**
 * «Training against competition»: pooled group stats of the Lag on training
 * conventions and on competitions (convention.isCompetition).
 * @returns null without Lag, else { training: stats | null, competition: stats | null } (stats = groupStats + lagCount)
 */
export function competitionSplit(lags, target) {
    const list = (Array.isArray(lags) ? lags : []).filter(Boolean);
    if (list.length === 0) return null;
    return {
        training: pooledStats(list.filter(l => !l.isCompetition), target),
        competition: pooledStats(list.filter(l => !!l.isCompetition), target),
    };
}

function dayKey(lag) {
    const ms = lag && lag.date ? Date.parse(lag.date) : NaN;
    return Number.isFinite(ms) ? new Date(ms).toISOString().slice(0, 10) : '';
}

/**
 * «Warm-up»: mean radius by Lag number within one day (stage order, then time),
 * averaged over the days with that many Lag.
 * @returns null unless some day has two Lag, else [{ position (1-based), meanRadiusMm, days }]
 */
export function warmupCurve(lags, target) {
    const days = new Map();
    (Array.isArray(lags) ? lags : []).forEach(lag => {
        const key = dayKey(lag);
        if (!key) return;
        if (!days.has(key)) days.set(key, []);
        days.get(key).push(lag);
    });
    const buckets = [];
    let multi = false;
    days.forEach(list => {
        if (list.length >= 2) multi = true;
        [...list]
            .sort((a, b) => (a.stageIdx || 0) - (b.stageIdx || 0) || Date.parse(a.date || 0) - Date.parse(b.date || 0))
            .forEach((lag, i) => {
                const stats = groupStats(lag.shots, target);
                if (!stats || stats.count < 2) return;
                if (!buckets[i]) buckets[i] = [];
                buckets[i].push(stats.meanRadiusMm);
            });
    });
    if (!multi) return null;
    return buckets
        .map((list, i) => (list && list.length ? { position: i + 1, meanRadiusMm: mean(list, v => v), days: list.length } : null))
        .filter(Boolean);
}

function modalCount(points) {
    const counts = new Map();
    points.forEach(p => counts.set(p.count, (counts.get(p.count) || 0) + 1));
    let best = null;
    counts.forEach((n, count) => {
        if (!best || n > best.n || (n === best.n && count > best.count)) best = { count, n };
    });
    return best ? best.count : 0;
}

/**
 * «Stability»: the Lag totals of the Lag with the usual shot count.
 * @param points progressSeries() output
 * @returns null under two such Lag, else { count, lagCount, mean, sd, min, max, label: 'stable' | 'varying' }
 */
export function stabilityStats(points) {
    const list = (Array.isArray(points) ? points : []).filter(p => p && Number.isFinite(p.total) && p.count > 0);
    const count = modalCount(list);
    const usual = list.filter(p => p.count === count);
    if (usual.length < 2) return null;
    const avg = mean(usual, p => p.total);
    const sd = sampleSd(usual, p => p.total, avg);
    return {
        count,
        lagCount: usual.length,
        mean: avg,
        sd,
        min: Math.min(...usual.map(p => p.total)),
        max: Math.max(...usual.map(p => p.total)),
        label: avg > 0 && sd / avg <= STABILITY_FRACTION ? 'stable' : 'varying',
    };
}

function sumValues(shots) {
    return shots.reduce((acc, s) => acc + (Number.isFinite(s.value) ? s.value : 0), 0);
}

/**
 * «Records» of one series type: best Lag total (among the Lag with the usual
 * shot count), best single series group, longest run of tens inside a group,
 * best Lag decimal average.
 * @returns null without Lag, else { bestLag, bestGroup, longestTenRun, bestDecimalAvg } — each null when unknown
 */
export function records(lags) {
    const list = (Array.isArray(lags) ? lags : []).filter(l => l && Array.isArray(l.shots) && l.shots.length);
    if (list.length === 0) return null;
    const stamp = lag => ({ date: lag.date || null, title: lag.title || '', stageName: lag.stageName || '' });
    const count = modalCount(list.map(l => ({ count: l.shots.length })));
    let bestLag = null;
    let bestGroup = null;
    let longestTenRun = null;
    let bestDecimalAvg = null;
    const groupCount = Math.max(...list.map(l => Math.max(0, ...groupsOf(l).map(g => g.shots.length))));
    list.forEach(lag => {
        const total = sumValues(lag.shots);
        if (lag.shots.length === count && (!bestLag || total > bestLag.total)) {
            bestLag = { total, count, ...stamp(lag) };
        }
        const decimals = lag.shots.filter(s => Number.isFinite(s.decimalValue));
        if (decimals.length) {
            const avg = mean(decimals, s => s.decimalValue);
            if (!bestDecimalAvg || avg > bestDecimalAvg.value) bestDecimalAvg = { value: avg, count: decimals.length, ...stamp(lag) };
        }
        groupsOf(lag).forEach(group => {
            const groupTotal = sumValues(group.shots);
            if (group.shots.length === groupCount && (!bestGroup || groupTotal > bestGroup.total)) {
                bestGroup = { total: groupTotal, count: group.shots.length, ...stamp(lag) };
            }
            let run = 0;
            group.shots.forEach(s => {
                run = Number.isFinite(s.value) && s.value >= 10 ? run + 1 : 0;
                if (run > 0 && (!longestTenRun || run > longestTenRun.length)) longestTenRun = { length: run, ...stamp(lag) };
            });
        });
    });
    return {
        bestLag, bestGroup, longestTenRun, bestDecimalAvg,
    };
}

/**
 * «Heatmap»: shots counted per square cell of the target plane (mm, y up).
 * @returns null without positioned shots, else { cellMm, max, cells: [{ cx, cy, count }] } — cx/cy = cell centre
 */
export function heatmapCells(shots, target) {
    const points = toMm(shots);
    if (points.length === 0) return null;
    const t = TARGETS[target] || TARGETS.duel;
    const cellMm = t.ringStep * HEATMAP_CELL_RING_FRACTION;
    const cells = new Map();
    points.forEach(p => {
        const ix = Math.floor(p.x / cellMm);
        const iy = Math.floor(p.y / cellMm);
        const key = `${ix}|${iy}`;
        if (!cells.has(key)) cells.set(key, { cx: (ix + 0.5) * cellMm, cy: (iy + 0.5) * cellMm, count: 0 });
        cells.get(key).count += 1;
    });
    const list = [...cells.values()];
    return { cellMm, max: Math.max(...list.map(c => c.count)), cells: list };
}
